<template>
  <div class="offer-discount">
    <template v-if="theme === 'default'">
      <BaseButton
        class="offer-discount__link"
        theme="link"
        @click="handleGetDiscount"
      >
        <strong>{{ $t('discount.payment_by_bonuses') }}</strong>
      </BaseButton>

      <div class="offer-discount__max-discount">
        {{ bonusesPercent }}%
      </div>
    </template>

    <template v-else>
      <BaseButton
        theme="link"
        @click="handleGetDiscount"
      >
        <strong class="offer-discount__link--mini">
          {{ $t('discount.payment_by_bonuses') }}
          <b class="offer-discount__max-discount--mini">
            {{ bonusesPercent }}%
          </b>
        </strong>
      </BaseButton>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { modalOfferDiscountId } from '@/constants/modalNames'
import type { Product, ProductCatalog } from '~/types/product'

type Props = {
  theme?: 'default' | 'small'
  offerUuid: string
  offerCode: string
  offerPrice: number
  maxBonusesPercent?: number
  product: Product | ProductCatalog
}

type Emits = {
  (eventName: 'click'): void
}

const props = withDefaults(defineProps<Props>(), {
  theme: 'default',
  maxBonusesPercent: 0
})
const emit = defineEmits<Emits>()
const { $vfm } = useNuxtApp()

// Computed
const bonusesPercent = computed<number>(() => {
  return Math.min(props.maxBonusesPercent, 99)
})

// Methods
const handleGetDiscount = () => {
  $vfm.open(modalOfferDiscountId, {
    uuid: props.offerUuid,
    code: props.offerCode,
    price: props.offerPrice,
    maxBonusesPercent: props.maxBonusesPercent,
    product: props.product
  })
  emit('click')
}
const handleClose = () => {
  $vfm.close(modalOfferDiscountId)
}

onBeforeUnmount(handleClose)
</script>

<style lang="scss">
.offer-discount {
  $root: &;

  &__link {
    font-weight: bold;
    &--mini {
      font-weight: 500;
      font-size: $font-size-small_syn;
      line-height: 120%;
    }
  }

  &__max-discount {
    font-weight: bold;
    text-align: right;
    font-size: $h1-font-size;
    line-height: 1;
    color: $font-accent-color;
    margin-bottom: 0;
    sup {
      line-height: 1;
      font-size: $h5-font-size;
      text-transform: lowercase;
      vertical-align: unset;
    }

    @media only screen and (max-width: 1024px) {
      margin-top: 0.5rem;
    }

    @media only screen and (min-width: 1024px) {
      margin-top: 0.85rem;
    }

    &--mini {
      color: $font-accent-color;
      sup {
        vertical-align: baseline;
      }
    }
  }
}
</style>
